import { z } from "zod";
import { RequiredFirestoreTimestampSchema } from "./common.schema";

/**
 * Persona status enum
 */
export const PersonaStatusSchema = z.enum(["pending", "completed", "error"]);
export const PersonaLifecycleStatusSchema = z.enum([
  "pending",
  "completed",
  "error",
  "archived",
  "implemented",
]);
export type PersonaStatus = z.infer<typeof PersonaStatusSchema>;

/**
 * Persona demographics schema
 */
export const PersonaDemographicsSchema = z.object({
  occupation: z.string(),
  age_range: z.string(),
  location: z.string(),
  industry: z.string(),
  company_size: z.string(),
  budget_range: z.string(),
});
export type PersonaDemographics = z.infer<typeof PersonaDemographicsSchema>;

/**
 * Persona document schema (without id)
 * Path: organizations/{orgId}/domains/{domainId}/personas/{personaId}
 * Note: Timestamps are converted to Date in the store
 */
export const PersonaDocSchema = z.object({
  user_id: z.string(),
  domain_id: z.string(),
  is_enabled: z.boolean(),
  name: z.string().default(""),
  tagline: z.string().default(""),
  pain: z.string().default(""),
  journey_context: z.string().default(""),
  fuel: z.array(z.string()).default([]),
  friction: z.array(z.string()).default([]),
  quote: z.string().default(""),
  demographics: PersonaDemographicsSchema.nullable().default(null),
  custom_note: z.string().default(""),
  status: PersonaLifecycleStatusSchema.default("pending"),
  error_message: z.string().default(""),
  created_at: RequiredFirestoreTimestampSchema,
  // Which conversation turn produced this document, when the assistant made it.
  // Backend-written only (these collections are `allow write: if false`), so the
  // value is trustworthy; null on anything a member made by hand and on
  // documents written before the fields existed.
  source_thread_id: z.string().nullable().optional(),
  source_message_id: z.string().nullable().optional(),
});
export type PersonaDoc = z.infer<typeof PersonaDocSchema>;

/**
 * Persona schema (with id)
 */
export const PersonaSchema = PersonaDocSchema.extend({ id: z.string() });
export const PersonaEntitySchema = PersonaSchema.extend({
  updated_at: RequiredFirestoreTimestampSchema.nullable().optional(),
});
export type Persona = z.infer<typeof PersonaEntitySchema>;

/**
 * Parse functions
 */
export const parsePersona = (data: unknown): Persona =>
  PersonaEntitySchema.parse(data);
export const safePersonaParse = (data: unknown) =>
  PersonaEntitySchema.safeParse(data);

// UI State type (not a Zod schema - internal app state)
export interface PersonaState {
  personas: Persona[];
  selectedPersonaId: string | null;
  isLoading: boolean;
  error: string | null;
}
